import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Toolbar,
  AppBar,
  Tabs,
  Tab,
  Paper,
  Slider,
  Stack,
  Divider,
  Tooltip,
  Chip,
  styled,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { awsDateToUS } from "utils/dateUtils";

// Set worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PolicyTypeChip = styled(Chip)(({ theme }) => ({
  fontSize: "0.65rem",
  height: 20,
  backgroundColor: theme.palette.info.light,
  color: theme.palette.info.dark,
  margin: "0 2px",
}));

const DocumentViewerModal = ({ open, document, onClose, onDownload }) => {
  const [tabValue, setTabValue] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!document) {
    return null;
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleZoomChange = (event, newValue) => {
    setZoom(newValue);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 10, 50));
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    
    if (!isFullscreen && document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: { bgcolor: "grey.100" },
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {document.filename}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Document" />
            <Tab label="Details" />
          </Tabs>
        </Box>
      </AppBar>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {tabValue === 0 && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* PDF Viewer Toolbar */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 1, 
                bgcolor: "background.paper", 
                display: "flex", 
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* Page Navigation */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
                <IconButton size="small" onClick={handlePreviousPage} disabled={currentPage <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2">
                  {currentPage} / {totalPages}
                </Typography>
                <IconButton size="small" onClick={handleNextPage} disabled={currentPage >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Zoom Controls */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
                <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 50}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Box sx={{ width: 80 }}>
                  <Slider
                    size="small"
                    value={zoom}
                    onChange={handleZoomChange}
                    min={50}
                    max={200}
                    step={10}
                  />
                </Box>
                <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 200}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 40 }}>
                  {zoom}%
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Actions */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Toggle Fullscreen">
                  <IconButton size="small" onClick={toggleFullscreen}>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print">
                  <IconButton size="small" onClick={handlePrint}>
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => onDownload(document)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>

            {/* PDF Display Area */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                bgcolor: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                p: 2,
              }}
            >
              {document.fileType?.toLowerCase() === 'application/pdf' || document.fileUrl?.toLowerCase().endsWith('.pdf') ? (
                <Box
                  sx={{
                    backgroundColor: 'white',
                    padding: 2,
                    borderRadius: 1,
                    '& .react-pdf__Document': {
                      backgroundColor: 'white',
                    },
                    '& .react-pdf__Page': {
                      backgroundColor: 'white',
                    },
                    '& .react-pdf__Page__canvas': {
                      backgroundColor: 'white',
                      filter: 'none',
                    },
                    '& canvas': {
                      backgroundColor: 'white !important',
                      filter: 'none !important',
                    }
                  }}
                >
                  <Document
                    file={document.fileUrl}
                    onLoadSuccess={({ numPages }) => setTotalPages(numPages)}
                    loading={
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress />
                      </Box>
                    }
                    error={
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <Typography color="error">Failed to load PDF</Typography>
                      </Box>
                    }
                  >
                    <Page 
                      pageNumber={currentPage} 
                      scale={zoom / 100}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      className="pdf-page"
                    />
                  </Document>
                </Box>
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    width: `${zoom}%`,
                    maxWidth: "100%",
                    height: "100%",
                    overflow: "hidden",
                    transition: "width 0.2s ease-in-out",
                    backgroundColor: "white",
                  }}
                >
                  <Box
                    component="iframe"
                    src={document.fileUrl}
                    sx={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      backgroundColor: "white",
                    }}
                    title="Document"
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    loading="lazy"
                  />
                </Paper>
              )}
            </Box>
          </Box>
        )}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Document Details
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", rowGap: 2, columnGap: 2, mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                File Name:
              </Typography>
              <Typography variant="body2">{document.filename}</Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Policy Types:
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {document.policyTypes?.map((type) => (
                  <PolicyTypeChip key={type} label={type} size="small" />
                ))}
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Uploaded Date:
              </Typography>
              <Typography variant="body2">
                {document.uploadedDate ? awsDateToUS(document.uploadedDate) : "-"}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                File Size:
              </Typography>
              <Typography variant="body2">{document.fileSize || "-"}</Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Document Type:
              </Typography>
              <Typography variant="body2">PDF</Typography>

              {document.uploadedBy && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Uploaded By:
                  </Typography>
                  <Typography variant="body2">{document.uploadedBy}</Typography>
                </>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewerModal;